import React from 'react'
import { StyledButtonRounded, SytledDotButton, StyledNumberButton } from './style'
import * as Vlab from 'vlab-frontend-components'
import Translator from '../i18n/translator';
import useWindowSize from '../../hooks/useWindowSize';

type Props = {
  currentPage: number
  totalPages: number
  pageSize: number
  labelRowsPerPage?: string
  total: number
  disabled?: boolean
  onPageChange?: (value: number) => void
  onRowsPerPageChange?: (value: number) => void
  rowsPerPageOptions?: Array<number>
};

const Pagination: React.FC<Props> = ({
  currentPage,
  totalPages,
  pageSize,
  disabled,
  total,
  onPageChange,
}: Props) => {
  const { width } = useWindowSize();
  const handlePageChange = (page: number) => {
    if (onPageChange) {
      if (page >= 1 && page <= totalPages) {
        onPageChange(page);
      }
    }
  };

  const renderPageButtons = (width: number) => {
    let visiblePageCount = 10;
    if (width < 1280) visiblePageCount = 8;
    if (width < 1080) visiblePageCount = 6;
    if (width < 920) visiblePageCount = 4;
    const startPage = Math.max(1, currentPage - Math.floor(visiblePageCount / 2));
    const endPage = Math.min(totalPages, startPage + visiblePageCount - 1);

    const buttons = [];

    if (startPage > 1) {
      buttons.push(<SytledDotButton key="ellipsis-left" disabled={disabled}>...</SytledDotButton>);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <StyledNumberButton
          data-testid={`index-button-${i}`}
          key={i}
          style={{ backgroundColor: i === currentPage ? '#ECF2F8' : 'transparent' }}
          onClick={() => handlePageChange(i)}
          disabled={disabled}
        >
          {i}
        </StyledNumberButton>
      );
    }

    if (endPage < totalPages && totalPages - endPage > 1) {
      buttons.push(<SytledDotButton key="ellipsis-right" disabled={disabled}>...</SytledDotButton>);
    }

    if (endPage < totalPages) {
      buttons.push(
        <StyledNumberButton
          data-testid={`index-button-${totalPages}`}
          key={totalPages}
          style={{ backgroundColor: totalPages === currentPage ? '#ECF2F8' : 'transparent' }}
          onClick={() => handlePageChange(totalPages)}
          disabled={disabled}
        >
          {totalPages}
        </StyledNumberButton>
      );
    }

    return buttons;
  };

  if (totalPages <= 0) return null;

  return (
    <div
      data-testid="pagination-container" 
      style={{
        backgroundColor: '#F7F8FC',
        width: '100%',
        height: '48px',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '8px'
      }}
    >
      <Vlab.Typography type='poppinsBodyS' color='greyDark' style={{ fontSize: 10}}>
        {pageSize} <Translator path='components.pagination.resultsPage' />
      </Vlab.Typography>
      <div
        data-testid="pagination" 
        style={{
          width: '80%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
        }}
      >
        <StyledButtonRounded disabled={disabled} data-testid="previous-button" onClick={() => handlePageChange(currentPage - 1)}>
          <Vlab.ArrowLeftIcon />
        </StyledButtonRounded>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{renderPageButtons(width)}</div>

        <StyledButtonRounded disabled={disabled} data-testid="next-button" onClick={() => handlePageChange(currentPage + 1)}>
          <Vlab.ArrowRightIcon />
        </StyledButtonRounded>
      </div>

      <Vlab.Typography type='poppinsBodyS' color='greyDark' style={{ fontSize: 10}}>
        {total} <Translator path='components.pagination.results' />
      </Vlab.Typography>
    </div>
  );
};

export { Pagination };